import logger from "@/utils/logger";
import { sendWhatsApp } from "@/services/whatsappNotification.service";
import { sendCompanyNotificationEmail } from "@/services/companyEmailNotification.service";
import { fetchCompanyMessagingSendSmsAlso } from "@/services/timesheetCreatedNotification.service";
import {
  formatMessageDisplayCalendarDate,
  formatMessageDateTime,
} from "@/services/consultantOnboardingNotification.helpers.js";

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const x = Number(n);
  if (Number.isNaN(x)) return "—";
  return x.toFixed(2);
}

function line(label, value) {
  const v = value != null && String(value).trim() !== "" ? String(value).trim() : "—";
  return `${label}: ${v}`;
}

function truncate(s, max) {
  const t = String(s || "").replace(/\r\n/g, "\n").trim();
  if (!t) return "";
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/**
 * Plain-text for company SMS/WhatsApp + SendGrid (HTML uses `Label: value` lines).
 * @param {object} p
 * @param {string|number} p.companyId
 * @param {string} [p.companyName]
 * @param {string} [p.vendorName]
 * @param {string} [p.billNo]
 * @param {string} [p.billDate] ISO date
 * @param {string} [p.dueDate]
 * @param {string} [p.deliveryDate]
 * @param {string} [p.paymentTermLabel]
 * @param {number|string} [p.total]
 * @param {string} [p.contactName]
 * @param {string} [p.phone]
 * @param {string} [p.comments]
 * @param {{ description?: string, amount?: number }[]} [p.accountLines]
 */
export function buildBillCompanyNotificationMessage(p) {
  const billNo = String(p.billNo || "").trim() || "—";
  const dateLabel = formatMessageDisplayCalendarDate(p.billDate) || p.billDate || "—";
  const dueLabel = formatMessageDisplayCalendarDate(p.dueDate) || p.dueDate || "—";
  const deliveryLabel =
    p.deliveryDate != null && String(p.deliveryDate).trim()
      ? formatMessageDisplayCalendarDate(p.deliveryDate) || p.deliveryDate
      : "";

  const lines = ["Bill details"];
  lines.push(
    line("Company", p.companyName),
    line("Vendor", p.vendorName),
    line("Bill #", billNo),
    line("Bill date", dateLabel),
    line("Due date", dueLabel)
  );
  if (deliveryLabel) {
    lines.push(line("Service / delivery date", deliveryLabel));
  }
  if (p.paymentTermLabel != null && String(p.paymentTermLabel).trim()) {
    lines.push(line("Payment terms", p.paymentTermLabel));
  }
  lines.push(line("Contact", p.contactName), line("Phone", p.phone));
  if (p.vendorEmail != null && String(p.vendorEmail).trim()) {
    lines.push(line("Vendor email", p.vendorEmail));
  }
  lines.push(line("Total (USD)", fmtMoney(p.total)));

  const accountLines = Array.isArray(p.accountLines) ? p.accountLines : [];
  if (accountLines.length) {
    lines.push("Expense lines");
    const max = 14;
    accountLines.slice(0, max).forEach((row, i) => {
      const desc = truncate(row?.description || `Line ${i + 1}`, 120);
      lines.push(line(desc, fmtMoney(row?.amount)));
    });
    if (accountLines.length > max) {
      lines.push(`… +${accountLines.length - max} more line(s)`);
    }
  }

  if (p.comments != null && String(p.comments).trim()) {
    lines.push(line("Comments", truncate(p.comments, 400)));
  }
  lines.push(line("Recorded at", formatMessageDateTime()));
  return lines.join("\n");
}

/**
 * Notify company messaging (WhatsApp + optional SMS) and Settings → Email recipients.
 * Optional PDF attachment (same pattern as invoice / receive payment).
 */
export async function notifyCompanyBillSaved(payload) {
  const companyId = payload?.companyId;
  if (companyId == null || companyId === "") return;

  const sendSMS = await fetchCompanyMessagingSendSmsAlso(companyId);
  const message = buildBillCompanyNotificationMessage(payload);
  const billNo = String(payload.billNo || "").trim() || "—";

  const attachments = [];
  const b64 = payload.pdfBase64 != null ? String(payload.pdfBase64).replace(/\s/g, "") : "";
  const fn = payload.pdfFileName != null ? String(payload.pdfFileName).trim() : "";
  if (b64 && fn) {
    attachments.push({
      content: b64,
      filename: fn.slice(0, 180),
      type: "application/pdf",
    });
  }

  try {
    await sendWhatsApp(message, Boolean(sendSMS), {
      ...(companyId != null && companyId !== "" ? { companyId: String(companyId) } : {}),
    });
  } catch (e) {
    logger.warn("notifyCompanyBillSaved: WhatsApp/SMS", e);
  }

  await sendCompanyNotificationEmail(message, companyId, `Anna Books: Bill ${billNo}`, {
    event: "bill",
    bannerTitle: `Bill ${billNo}`,
    ...(attachments.length ? { attachments } : {}),
  });
}
